import { NgClass } from '@angular/common'
import { Component, inject, Input } from '@angular/core'
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { SmsEditorComponent } from '@app/v2/shared/components/sms-editor/sms-editor.component'
import { SmsMessageType } from '@app/v2/shared/enums/sms-message.enum'
import { SmsPreviewEvent } from '@app/v2/shared/types/sms-editor.type'
import { SmsMessageModel } from '@app/v2/shared/types/sms-message.model'

/** One flag inside sms_communication on the persona save payload. */
type SmsFlag = 'enable_online' | 'enable_onsite'

/**
 * SMS channel inside persona Confirmations & Reminders.
 * Switch values and the message live on the persona form and are sent as sms_communication.
 */
@Component({
  selector: 'ngx-sms-integration',
  templateUrl: './sms-integration.component.html',
  host: { class: 'tw-block tw-w-full' },
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, SmsEditorComponent]
})
export class SmsIntegrationComponent {
  /** Persona flags and message saved as sms_communication. */
  @Input({ required: true }) smsCommunication: UntypedFormGroup

  /** Event that owns this persona. Name and slug are inserted as plain text in the preview. */
  @Input() event: SmsPreviewEvent = null

  readonly sampleGuestName = SmsMessageModel.SAMPLE_GUEST_FULL_NAME
  readonly messageType = SmsMessageType.CONFIRMATION

  private readonly _sanitizer = inject(DomSanitizer)

  /**
   * Form control for one SMS flag on the persona save payload.
   * @param name Flag inside sms_communication
   */
  smsControl(name: SmsFlag): UntypedFormControl {
    return this.smsCommunication.get(name) as UntypedFormControl
  }

  /**
   * Editable confirmation body.
   */
  get messageControl(): UntypedFormControl {
    return this.smsCommunication.get('message') as UntypedFormControl
  }

  /**
   * Phone-preview bubble. Sample links are painted blue. `text` is the editor's filled-in sample.
   * @param text Resolved SMS from app-sms-editor
   */
  previewHtml(text: string): SafeHtml {
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const html = escaped.replace(/https?:\/\/[^\s]+/g, '<span style="color:#1C64F2;overflow-wrap:anywhere;word-break:break-all">$&</span>')
    return this._sanitizer.bypassSecurityTrustHtml(html)
  }
}
